import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardHeader } from "@/components/ui/Card";
import { Dialog } from "@/components/ui/Dialog";
import { Field } from "@/components/ui/Field";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Table, Td, Th } from "@/components/ui/Table";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import { teams as teamsApi, users as usersApi } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { strings } from "@/lib/strings";
import type { BulkUserImportResult, Team, User } from "@/lib/types";

/** User administration (docs/08 §8.10, A-01). */
export function UserAdmin() {
  const { hasRole, user: me } = useAuth();
  const { notify } = useToast();
  const isAdmin = hasRole("administrator");

  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [invite, setInvite] = useState<{ email: string; token: string; expiresAt?: string } | null>(null);
  const [form, setForm] = useState({ email: "", full_name: "", role: "volunteer", team_id: "" });
  const [busy, setBusy] = useState(false);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkUserImportResult | null>(null);

  const load = useCallback(async () => {
    try {
      const [userList, teamList] = await Promise.all([usersApi.list(), teamsApi.list()]);
      setUsers(userList);
      setTeams(teamList);
    } catch {
      notify(strings.errors.forbidden, "error");
    }
  }, [notify]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const result = await usersApi.create({
        email: form.email.trim(),
        full_name: form.full_name.trim(),
        role: form.role,
        team_id: form.team_id || null,
      });
      // The signup link is shown once and never stored in plaintext.
      setInvite({
        email: result.user.email,
        token: result.invite_token,
        expiresAt: result.invite_expires_at,
      });
      setAddOpen(false);
      setForm({ email: "", full_name: "", role: "volunteer", team_id: "" });
      await load();
    } catch (error) {
      notify(error instanceof Error ? error.message : strings.errors.generic, "error");
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async (target: User) => {
    try {
      await usersApi.deactivate(target.id);
      notify(`${target.full_name} deactivated. Their entries and audit trail are kept.`, "success");
      await load();
    } catch {
      notify(strings.errors.forbidden, "error");
    }
  };

  const reinvite = async (target: User) => {
    try {
      const result = await usersApi.reinvite(target.id);
      setInvite({
        email: result.user.email,
        token: result.invite_token,
        expiresAt: result.invite_expires_at,
      });
    } catch {
      notify(strings.errors.forbidden, "error");
    }
  };

  const teamName = (id: string | null) =>
    id ? (teams.find((team) => team.id === id)?.name ?? "—") : "—";

  const downloadTemplate = () => {
    const csvContent =
      "email,full_name,role,team\n" +
      "volunteer.one@eksuth.org,Adebayo Ogun,volunteer,t1\n" +
      "volunteer.two@eksuth.org,Bimpe Alabi,volunteer,t2\n" +
      "lead.one@eksuth.org,Dr Kolawole,team_lead,Team 1\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "bulk_users_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) return;
    setBulkBusy(true);
    try {
      const result = await usersApi.bulkCsv(bulkFile);
      setBulkResult(result);
      await load();
      if (result.total_created > 0 && result.total_failed === 0) {
        notify(`Successfully imported ${result.total_created} user(s).`, "success");
      } else if (result.total_created > 0 && result.total_failed > 0) {
        notify(
          `Imported ${result.total_created} user(s), with ${result.total_failed} error(s).`,
          "info",
        );
      } else {
        notify(`Import failed: ${result.total_failed} error(s).`, "error");
      }
    } catch (error) {
      notify(error instanceof Error ? error.message : strings.errors.generic, "error");
    } finally {
      setBulkBusy(false);
    }
  };

  const downloadInviteLinks = () => {
    if (!bulkResult || bulkResult.created.length === 0) return;
    const header = "email,full_name,role,team,invite_url,invite_expires_at\n";
    const rows = bulkResult.created.map((item) => {
      const inviteUrl = `${window.location.origin}/signup?token=${item.invite_token}`;
      return `"${item.user.email}","${item.user.full_name}","${item.user.role}","${item.team_name ?? ""}","${inviteUrl}","${item.invite_expires_at}"`;
    });
    const csvContent = header + rows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "user_invite_links.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-slate-900">{strings.admin.users}</h1>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setBulkOpen(true)}>
            Bulk Add (CSV)
          </Button>
          <Button onClick={() => setAddOpen(true)}>{strings.admin.addUser}</Button>
        </div>
      </div>

      <Card>
        <CardHeader
          title="Accounts"
          description="No account is ever deleted. Deactivating preserves the audit trail."
        />
        <Table caption="User accounts">
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Role</Th>
              <Th>Team</Th>
              <Th>Last login</Th>
              <Th>Status</Th>
              <Th>
                <span className="sr-only">Actions</span>
              </Th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <Td>{user.full_name}</Td>
                <Td className="text-xs">{user.email}</Td>
                <Td>
                  {isAdmin ? (
                    <Select
                      aria-label={`Role for ${user.full_name}`}
                      value={user.role}
                      onChange={async (event) => {
                        try {
                          await usersApi.update(user.id, { role: event.target.value });
                          notify(`Role updated for ${user.full_name}.`, "success");
                          await load();
                        } catch (error) {
                          notify(
                            error instanceof Error ? error.message : strings.errors.generic,
                            "error",
                          );
                        }
                      }}
                      options={[
                        { value: "volunteer", label: "Volunteer" },
                        { value: "team_lead", label: "Team lead" },
                        { value: "data_manager", label: "Data manager" },
                        { value: "microbiologist", label: "Microbiologist" },
                        { value: "administrator", label: "Administrator" },
                      ]}
                      className="h-9 text-xs"
                    />
                  ) : (
                    user.role.replace(/_/g, " ")
                  )}
                </Td>
                <Td>
                  {isAdmin ? (
                    <Select
                      aria-label={`Team for ${user.full_name}`}
                      value={user.team_id ?? ""}
                      placeholder={
                        user.role === "volunteer" || user.role === "team_lead"
                          ? "Select a team"
                          : "— No team —"
                      }
                      onChange={async (event) => {
                        const newTeamId = event.target.value || null;
                        try {
                          await usersApi.update(user.id, { team_id: newTeamId });
                          notify(`Team updated for ${user.full_name}.`, "success");
                          await load();
                        } catch (error) {
                          notify(
                            error instanceof Error ? error.message : strings.errors.generic,
                            "error",
                          );
                        }
                      }}
                      options={teams.map((team) => ({
                        value: team.id,
                        label: team.name,
                      }))}
                      className="h-9 text-xs min-w-[140px]"
                    />
                  ) : (
                    teamName(user.team_id)
                  )}
                </Td>
                <Td className="whitespace-nowrap text-xs">
                  {user.last_login_at ? formatDateTime(user.last_login_at) : "Never"}
                </Td>
                <Td>
                  {user.is_active ? (
                    <Badge tone="ok">Active</Badge>
                  ) : (
                    <Badge>Inactive</Badge>
                  )}
                  {user.totp_enabled && <Badge tone="info" className="ml-1">2FA</Badge>}
                </Td>
                <Td className="whitespace-nowrap">
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => void reinvite(user)}>
                      {strings.admin.reinvite}
                    </Button>
                    {user.is_active && user.id !== me?.id && (
                      <Button size="sm" variant="ghost" onClick={() => void deactivate(user)}>
                        {strings.admin.deactivate}
                      </Button>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      <Dialog
        open={addOpen}
        onOpenChange={setAddOpen}
        title={strings.admin.addUser}
        description="They will receive a one-time link (valid for 7 days / 1 week) to set their own password and sign the confidentiality agreement."
        footer={
          <>
            <Button variant="secondary" onClick={() => setAddOpen(false)}>
              {strings.common.cancel}
            </Button>
            <Button loading={busy} onClick={() => void create()}>
              {strings.admin.addUser}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field id="new-name" label="Full name" required>
            <Input
              id="new-name"
              value={form.full_name}
              onChange={(event) => setForm({ ...form, full_name: event.target.value })}
            />
          </Field>
          <Field id="new-email" label="Hospital email" required>
            <Input
              id="new-email"
              type="email"
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
            />
          </Field>
          <Field id="new-role" label="Role" required>
            <Select
              id="new-role"
              value={form.role}
              onChange={(event) => setForm({ ...form, role: event.target.value })}
              options={
                isAdmin
                  ? [
                      { value: "volunteer", label: "Volunteer" },
                      { value: "team_lead", label: "Team lead" },
                      { value: "data_manager", label: "Data manager" },
                      { value: "microbiologist", label: "Microbiologist" },
                      { value: "administrator", label: "Administrator" },
                    ]
                  : [
                      { value: "volunteer", label: "Volunteer" },
                      { value: "team_lead", label: "Team lead" },
                    ]
              }
            />
          </Field>
          <Field
            id="new-team"
            label="Team"
            hint="Required for volunteers and team leads."
          >
            <Select
              id="new-team"
              placeholder="No team"
              value={form.team_id}
              onChange={(event) => setForm({ ...form, team_id: event.target.value })}
              options={teams.map((team) => ({ value: team.id, label: team.name }))}
            />
          </Field>
        </div>
      </Dialog>

      <Dialog
        open={invite !== null}
        onOpenChange={() => setInvite(null)}
        title="Signup link"
        description={strings.admin.inviteCreated}
      >
        <p className="text-sm text-slate-700">
          Recipient: <span className="font-semibold text-slate-900">{invite?.email}</span>
        </p>
        <div className="mt-2 flex items-center justify-between rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800">
          <span>
            Valid for <strong>7 days</strong> (1 week)
            {invite?.expiresAt ? ` · Expires ${formatDateTime(invite.expiresAt)}` : ""}
          </span>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => {
              if (invite?.token) {
                void navigator.clipboard.writeText(`${window.location.origin}/signup?token=${invite.token}`);
                notify(`Copied signup link for ${invite.email}`, "success");
              }
            }}
          >
            Copy link
          </Button>
        </div>
        <code className="mt-2 block break-all rounded bg-slate-100 p-3 font-mono text-xs text-slate-800 select-all border border-slate-200">
          {window.location.origin}/signup?token={invite?.token}
        </code>
      </Dialog>

      <Dialog
        open={bulkOpen}
        onOpenChange={(open) => {
          setBulkOpen(open);
          if (!open) {
            setBulkFile(null);
            setBulkResult(null);
          }
        }}
        title="Bulk Add Users via CSV"
        description="Upload a CSV spreadsheet with email, full_name, role, and team columns."
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setBulkOpen(false);
                setBulkFile(null);
                setBulkResult(null);
              }}
            >
              {bulkResult ? "Close" : strings.common.cancel}
            </Button>
            {bulkResult ? (
              <Button
                onClick={() => {
                  setBulkOpen(false);
                  setBulkFile(null);
                  setBulkResult(null);
                }}
              >
                Done
              </Button>
            ) : (
              <Button
                loading={bulkBusy}
                disabled={!bulkFile || bulkBusy}
                onClick={() => void handleBulkUpload()}
              >
                Upload & Create Users
              </Button>
            )}
          </>
        }
      >
        <div className="space-y-4 text-sm">
          {!bulkResult ? (
            <>
              <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-700 space-y-2 border border-slate-200">
                <p className="font-semibold text-slate-900">CSV Format Instructions:</p>
                <ul className="list-disc pl-4 space-y-1">
                  <li>
                    <strong>email</strong>: Valid staff email address (e.g. <code>staff@eksuth.org</code>).
                  </li>
                  <li>
                    <strong>full_name</strong>: Full name of the staff member.
                  </li>
                  <li>
                    <strong>role</strong>: <code>volunteer</code>, <code>team_lead</code>, <code>data_manager</code>, or <code>microbiologist</code>.
                  </li>
                  <li>
                    <strong>team</strong>: Team slug or name (e.g. <code>t1</code>, <code>Team 1</code>). Required for volunteers and team leads.
                  </li>
                </ul>
              </div>

              <div className="flex items-center justify-between pt-1">
                <span className="text-xs text-slate-500">Need a template to get started?</span>
                <Button type="button" variant="secondary" size="sm" onClick={downloadTemplate}>
                  Download Sample CSV
                </Button>
              </div>

              <div className="pt-2">
                <label className="block text-xs font-medium text-slate-700 mb-1">Select CSV file</label>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  disabled={bulkBusy}
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;
                    setBulkFile(file);
                  }}
                  className="block w-full text-xs text-slate-600 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer border border-slate-300 rounded-md p-1"
                />
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Badge tone={bulkResult.total_created > 0 ? "ok" : "neutral"}>
                  {bulkResult.total_created} Created
                </Badge>
                {bulkResult.total_failed > 0 && (
                  <Badge tone="warning">
                    {bulkResult.total_failed} Failed
                  </Badge>
                )}
              </div>

              {bulkResult.created.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-slate-800">
                        Generated Single-Use Invite Links
                      </p>
                      <p className="text-[11px] text-emerald-700 font-medium">
                        Valid for 7 days (1 week)
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={downloadInviteLinks}
                    >
                      Download Invite Links CSV
                    </Button>
                  </div>
                  <div className="max-h-48 overflow-y-auto rounded border border-slate-200 bg-white text-xs divide-y divide-slate-100">
                    {bulkResult.created.map((item) => {
                      const link = `${window.location.origin}/signup?token=${item.invite_token}`;
                      return (
                        <div key={item.user.id} className="p-2 flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-medium text-slate-800 truncate">{item.user.full_name} ({item.user.email})</p>
                            <p className="text-[11px] text-slate-500">{item.user.role} &middot; {item.team_name ?? "No Team"} &middot; Expires in 7 days</p>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              void navigator.clipboard.writeText(link);
                              notify(`Copied invite link for ${item.user.email}`, "success");
                            }}
                          >
                            Copy link
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {bulkResult.errors.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-rose-700">Row Errors:</p>
                  <div className="max-h-36 overflow-y-auto rounded border border-rose-200 bg-rose-50 p-2 text-xs space-y-1">
                    {bulkResult.errors.map((err, i) => (
                      <div key={i} className="text-rose-800">
                        Row {err.row}{err.email ? ` (${err.email})` : ""}: {err.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
}
